import type { Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../shared.api';
import { toListParams } from '../policies/list-params';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type PagedListState<T> = { status: 'loading' } | { status: 'loaded'; page: Page<T>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: object) => toListParams(query).toString();

function toErrorState(err: unknown): PagedListState<never> {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/**
 * The page a list screen's query (`useListQuery`) asks for, re-fetched whenever that query or `load` changes.
 * `load` must be stable between renders (module-level, or `useCallback` over what it closes on).
 * The first load is `loading` (show a skeleton); later ones keep the old rows and report `isRefreshing`.
 */
export function usePagedList<Q extends object, T>(query: Q, load: (query: Q) => Promise<Page<T>>) {
  const [state, setState] = useState<PagedListState<T>>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    load(query)
      .then((page) => {
        if (!cancelled) setState({ status: 'loaded', page, queryKey: keyOf(query) });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query, load]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', page: await load(query), queryKey: keyOf(query) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, isRefreshing, reload };
}
